import { InjectionToken, Provider, Type } from '@angular/core';

import { TenantUser } from '../tenant/instance-profile';

/**
 * Lo mínimo que la sesión sabe del usuario: es el `me` que manda el API, y de él sólo lee el perfil
 * de la instancia (`TenantConfig`). El app lo tipa con su propio modelo (`User`).
 */
export type SessionUser = TenantUser & { id?: number | string };

/**
 * Las rutas a las que mandan los guards de la sesión. Vienen del app: la lib no sabe qué pantallas
 * tiene cada producto (Movilidad, Passenger).
 */
export interface SessionRoutes<U extends SessionUser = SessionUser> {
  /** A dónde manda `authGuard` cuando no hay sesión. */
  signedOut: string;
  /** La pantalla de inicio de un usuario con sesión: a dónde manda `guestGuard`. */
  home(user: U): string;
  /**
   * Un paso que el usuario con sesión tiene que cumplir antes de entrar (p. ej. capturar su fecha de
   * nacimiento). `authGuard` lo impone salvo que ya se esté navegando a él; null = ninguno.
   */
  pendingStep?(user: U | null): string | null;
}

export interface SessionConfig<U extends SessionUser = SessionUser> {
  routes: SessionRoutes<U>;
  /**
   * Llaves de Preferences propias del app que el logout borra junto con `me` y el token, en la misma
   * tanda (p. ej. caches del usuario que no deben pasar al siguiente en un dispositivo compartido).
   */
  clearOnLogout?: readonly string[];
}

/**
 * Lo que el app hace alrededor de la sesión y la lib no puede hacer (no importa pantallas, push ni
 * controladores de Ionic). `SessionService.logout()` los llama en este orden, esperando cada uno:
 *
 * 1. `beforeLogout`: la sesión sigue intacta (el token sigue guardado): p. ej. dar de baja el push en el API.
 * 2. (lib) el usuario sale de memoria: `me` se borra, `TenantConfig` vuelve a sus valores y `user$` emite
 *    null, sin esperar (como hacía `UserService.removeFromLocalStorage()`).
 * 3. `onLogout`: limpiar el estado del app (caché de queries, stores).
 * 4. (lib) se borran `me`, el token y `clearOnLogout` en una sola tanda.
 * 5. `afterLogout`: cerrar lo que quede abierto y navegar.
 */
export interface SessionHooks {
  beforeLogout?(): void | Promise<void>;
  onLogout?(): void | Promise<void>;
  afterLogout?(): void | Promise<void>;
  /**
   * Un 401 del API, ya sin repetidos (uno cada `UNAUTHORIZED_DEBOUNCE_MS` aunque fallen varias
   * peticiones a la vez). Sin este hook la sesión se cierra directo con `logout()`.
   */
  onUnauthorized?(): void;
}

export const SESSION_CONFIG = new InjectionToken<SessionConfig>('SESSION_CONFIG');

/**
 * Se resuelve tarde (al cerrar sesión o con un 401), no al crear `SessionService`: lo que el app pone
 * aquí suele depender de stores que a su vez leen la sesión, y resolverlo al construir sería un ciclo.
 */
export const SESSION_HOOKS = new InjectionToken<SessionHooks>('SESSION_HOOKS');

/** Para `main.ts`: las rutas y llaves del app y, opcional, la clase inyectable con sus hooks. */
export function provideSession<U extends SessionUser>(
  config: SessionConfig<U>,
  hooks?: Type<SessionHooks>
): Provider[] {
  return [
    { provide: SESSION_CONFIG, useValue: config },
    ...(hooks ? [{ provide: SESSION_HOOKS, useExisting: hooks }] : []),
  ];
}
